import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Download,
  RefreshCw,
  X,
  MailCheck,
  MailWarning,
  Users,
  Clock,
  Copy,
} from "lucide-react";
import { API } from "@/lib/auth";

/** Format an ISO string as "10 Feb 2026 · 23:47" in the viewer's local time. */
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "—";
  }
}

function relativeTime(iso) {
  if (!iso) return "";
  const now = new Date();
  const d = new Date(iso);
  const s = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
}

export default function FreeEducationSubscribers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/newsletter?list=free_education`);
      setData(r.data);
    } catch (e) {
      toast.error("Could not load Free Education subscribers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    const src = data?.subscribers || [];
    if (!q.trim()) return src;
    const t = q.trim().toLowerCase();
    return src.filter(
      (s) =>
        (s.email || "").toLowerCase().includes(t) ||
        (s.name || "").toLowerCase().includes(t)
    );
  }, [data, q]);

  const welcomedCount = useMemo(
    () => (data?.subscribers || []).filter((s) => s.free_education_welcome_sent_at).length,
    [data]
  );

  const exportCsv = () => {
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      // RFC 4180: wrap in quotes and double any embedded quotes
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = [
      "Email,Name,Subscribed at (ISO),Welcome sent at (ISO),Status,Sources",
      ...(data?.subscribers || []).map((s) => {
        const srcs = (s.sources && s.sources.length ? s.sources : [s.source]).filter(Boolean).join("|");
        return [
          esc(s.email),
          esc(s.name || ""),
          esc(s.subscribed_at || ""),
          esc(s.free_education_welcome_sent_at || ""),
          esc(s.unsubscribed ? "unsubscribed" : "active"),
          esc(srcs),
        ].join(",");
      }),
    ];
    const blob = new Blob([rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `free-education-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (!data && loading) {
    return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading subscribers…</div>;
  }
  if (!data) {
    return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading subscribers…</div>;
  }

  const total = data.total_active + data.total_unsubscribed;

  return (
    <div className="space-y-5" data-testid="fe-subscribers-panel">
      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="flex items-center gap-2 text-[hsl(var(--gold))]">
            <Users size={14} />
            <span className="overline">Active</span>
          </div>
          <div className="font-display text-5xl mt-3" data-testid="fe-sub-active-count">
            {data.total_active}
          </div>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-2">{total} lifetime</div>
        </div>
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="flex items-center gap-2 text-[hsl(var(--gold))]">
            <MailCheck size={14} />
            <span className="overline">Welcome email sent</span>
          </div>
          <div className="font-display text-5xl mt-3">{welcomedCount}</div>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-2">
            {data.total_active - welcomedCount > 0
              ? `${data.total_active - welcomedCount} pending / failed`
              : "All caught up"}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="flex items-center gap-2 text-[hsl(var(--gold))]">
            <MailWarning size={14} />
            <span className="overline">Unsubscribed</span>
          </div>
          <div className="font-display text-5xl mt-3">{data.total_unsubscribed}</div>
          <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-2">Excluded from campaigns</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search
            size={14}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--onyx)/0.4)]"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            data-testid="fe-sub-search"
            className="w-full h-11 rounded-full bg-white border border-[hsl(var(--onyx)/0.1)] pl-10 pr-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
          />
        </div>
        <button
          onClick={load}
          className="btn-pill btn-outline !text-xs"
          data-testid="fe-sub-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
        <button
          onClick={exportCsv}
          className="btn-pill btn-primary !text-xs"
          data-testid="fe-sub-export"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] overflow-hidden">
        <div className="grid grid-cols-[1.4fr_1.6fr_1.1fr_0.9fr_0.4fr] gap-3 px-5 py-3 border-b border-[hsl(var(--onyx)/0.06)] text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] bg-[hsl(var(--blush)/0.15)]">
          <div>Name</div>
          <div>Email</div>
          <div>Subscribed</div>
          <div>Welcome email</div>
          <div className="text-right">Details</div>
        </div>

        <div className="max-h-[560px] overflow-y-auto" data-testid="fe-sub-list">
          {list.length === 0 ? (
            <div className="py-16 text-center text-[hsl(var(--onyx)/0.45)] text-sm">
              {data.subscribers.length === 0
                ? "No Free Education subscribers yet. Share the /free-education link on Instagram!"
                : "No matches for that search."}
            </div>
          ) : (
            list.map((s, i) => {
              const subscribed = formatDateTime(s.subscribed_at);
              const rel = relativeTime(s.subscribed_at);
              const welcomed = !!s.free_education_welcome_sent_at;
              return (
                <button
                  key={s.email + i}
                  onClick={() => setSelected(s)}
                  data-testid={`fe-sub-row-${i}`}
                  className={`w-full text-left grid grid-cols-[1.4fr_1.6fr_1.1fr_0.9fr_0.4fr] gap-3 px-5 py-4 border-b border-[hsl(var(--onyx)/0.05)] text-sm transition-colors hover:bg-[hsl(var(--blush)/0.15)] ${
                    s.unsubscribed ? "opacity-60" : ""
                  }`}
                >
                  <div className="truncate font-medium">
                    {s.name || <span className="text-[hsl(var(--onyx)/0.35)] italic">no name</span>}
                  </div>
                  <div className="truncate text-[hsl(var(--onyx)/0.75)]">{s.email}</div>
                  <div className="text-[hsl(var(--onyx)/0.7)]">
                    <div>{subscribed}</div>
                    <div className="text-[10px] text-[hsl(var(--onyx)/0.45)] mt-0.5">{rel}</div>
                  </div>
                  <div>
                    {welcomed ? (
                      <span className="inline-flex items-center gap-1 text-[hsl(var(--gold))]">
                        <MailCheck size={12} /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[hsl(var(--burgundy))]">
                        <MailWarning size={12} /> Not sent
                      </span>
                    )}
                    {s.unsubscribed && (
                      <div className="text-[10px] text-[hsl(var(--burgundy))] mt-0.5">Unsubscribed</div>
                    )}
                  </div>
                  <div className="text-right text-[hsl(var(--onyx)/0.4)] text-xs">View →</div>
                </button>
              );
            })
          )}
        </div>

        <div className="px-5 py-3 text-[11px] text-[hsl(var(--onyx)/0.5)] flex items-center gap-2 border-t border-[hsl(var(--onyx)/0.05)]">
          <Clock size={11} /> Times shown in your local timezone. Data refreshes on demand.
          <span className="ml-auto">
            Showing <strong>{list.length}</strong> of {data.subscribers.length}
          </span>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <SubscriberDetail
          sub={selected}
          onClose={() => setSelected(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}

function SubscriberDetail({ sub, onClose, onRefresh }) {
  const [resending, setResending] = useState(false);
  const sources = (sub.sources && sub.sources.length ? sub.sources : [sub.source]).filter(Boolean);
  const copy = (value, label) => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  const resendWelcome = async () => {
    if (!window.confirm(`Resend the Free Education welcome email (with the AFTERCAREONME code) to ${sub.email}?`)) return;
    setResending(true);
    try {
      const r = await axios.post(
        `${API}/admin/newsletter/${encodeURIComponent(sub.email)}/resend-welcome`
      );
      toast.success("Welcome email resent");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      data-testid="fe-sub-detail"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md bg-[hsl(var(--cream))] shadow-2xl overflow-y-auto p-8">
        <button
          onClick={onClose}
          data-testid="fe-sub-detail-close"
          className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white border border-[hsl(var(--onyx)/0.1)] flex items-center justify-center hover:bg-[hsl(var(--blush)/0.4)]"
        >
          <X size={16} />
        </button>

        <div className="overline text-[hsl(var(--gold))]">Free Education subscriber</div>
        <h2 className="font-display text-3xl mt-2 leading-tight">
          {sub.name || <span className="italic text-[hsl(var(--onyx)/0.55)]">No name given</span>}
        </h2>
        <div className="text-[hsl(var(--onyx)/0.7)] text-sm mt-2 break-all">{sub.email}</div>

        <div className="mt-6 space-y-4">
          <DetailRow label="Subscribed at" value={formatDateTime(sub.subscribed_at)} sub={relativeTime(sub.subscribed_at)} />
          <DetailRow
            label="Welcome email sent"
            value={sub.free_education_welcome_sent_at ? formatDateTime(sub.free_education_welcome_sent_at) : "Not sent yet"}
            sub={sub.free_education_welcome_sent_at ? relativeTime(sub.free_education_welcome_sent_at) : "Retry on next signup with same email"}
            good={!!sub.free_education_welcome_sent_at}
          />
          <DetailRow
            label="Status"
            value={sub.unsubscribed ? "Unsubscribed" : "Active"}
            good={!sub.unsubscribed}
          />
          <DetailRow
            label="Signup source(s)"
            value={sources.join(" · ") || "unknown"}
          />
        </div>

        <div className="mt-8 space-y-2">
          <button
            onClick={resendWelcome}
            disabled={resending}
            data-testid="fe-sub-resend-welcome"
            className="btn-pill btn-primary w-full justify-center disabled:opacity-60"
          >
            {resending ? "Sending…" : (sub.free_education_welcome_sent_at ? "Resend welcome email" : "Send welcome email now")}
          </button>
          <button
            onClick={() => copy(sub.email, "Email")}
            className="btn-pill btn-outline w-full justify-center"
            data-testid="fe-sub-copy-email"
          >
            <Copy size={12} /> Copy email address
          </button>
          <a
            href={`mailto:${sub.email}?subject=NøoksPro%20Free%20Education`}
            className="btn-pill btn-outline w-full justify-center"
            data-testid="fe-sub-mailto"
          >
            Compose a personal reply
          </a>
        </div>

        <p className="mt-6 text-[11px] text-[hsl(var(--onyx)/0.45)] leading-relaxed">
          To send this subscriber a bulk campaign, use Marketing → Send campaign → NøoksPro Free Education.
        </p>
      </div>
    </div>
  );
}

function DetailRow({ label, value, sub, good }) {
  return (
    <div className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4">
      <div className="overline text-[hsl(var(--onyx)/0.55)]">{label}</div>
      <div className={`mt-1.5 text-sm font-medium ${good === false ? "text-[hsl(var(--burgundy))]" : ""}`}>
        {value}
      </div>
      {sub ? (
        <div className="text-[11px] text-[hsl(var(--onyx)/0.5)] mt-1">{sub}</div>
      ) : null}
    </div>
  );
}
